import QRCode from "qrcode.react";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Trans } from "@lingui/macro";
import { Alert } from "components/UI/Alert/Alert";
import { Button } from "components/UI/Button/Button";
import {
  IdSalesState,
  typeFullSalesItem,
} from "model/dashboardSales/dSalesReducer";
import { AvailableRoles } from "constants/roles";
import { ISystemState } from "model/system/systemReducer";
import { CheckProductItem } from "./CheckProductItem";
import { numberCurrencyFormat } from "constants/convertToLocalCurrency";
import { Loader } from "components/UI/Loader/Loader";
import { getSaleById } from "model/dashboardOrders/dOrdersAPI";

// Type
export type typePopupPrint = {
  variant?: string;
  setOpenPopup: React.Dispatch<React.SetStateAction<boolean>>;
};

// Component
export const PopupPrint: React.FC<typePopupPrint> = ({ setOpenPopup }) => {
  const dataObject = useSelector(
    (state: { dSales: IdSalesState }) => state.dSales.detailItem,
  );

  const [dataObjectFull, setDataObjectFull] =
    useState<typeFullSalesItem | null>(null);
  const [loader, setLoader] = useState(false);

  const [vat, setVat] = useState(0);

  useEffect(() => {
    const setData = async () => {
      if (dataObject?.id) {
        setLoader(true);

        const response = await getSaleById({ saleId: dataObject.id });

        if (response.status === 200) {
          const responseData = response.data as typeFullSalesItem;
          setDataObjectFull(response.data);

          let vat = 0;

          const discountPercent =
            dataObject!.discountAmount > 0
              ? dataObject!.discountAmount / responseData.amount
              : 0;

          vat = responseData.productList.reduce((acc, curr) => {
            const currVat =
              curr.tax !== null
                ? curr.tax
                : curr.product?.vat
                  ? curr.product.vat
                  : 0;
            const currPrice =
              (curr.customTotal ? curr.customTotal / curr.amount : curr.price) *
              (1 - discountPercent);
            const vatForItem = (currVat * currPrice) / (1 + currVat);
            return acc + vatForItem * curr.amount;
          }, 0);
          setVat(vat);
        }
      }
    };
    setData().finally(() => {
      setLoader(false);
    });

    // eslint-disable-next-line
  }, []);

  const userInfo = useSelector(
    (state: { system: ISystemState }) => state.system.userInfo,
  );

  const isSuperAdmin = userInfo?.authorizedUser?.roles.includes(
    AvailableRoles.ROLE_TINDA_SUPER_ADMIN,
  );

  const qrDate = dataObject?.date.substring(0, 19).replace(/[T,:,-]/gi, "");
  const qrLink = `https://ofd.soliq.uz/check?t=${dataObjectFull?.pos?.fiscalCardId}&r=${dataObjectFull?.receiptNumber}&c=${qrDate}&s=${dataObjectFull?.fiscalSign}`;

  return (
    <>
      {dataObject ? (
        <div className="w-80">
          <div className="border-b text-sm pb-2">
            <div className="text-gray-90 text-center text-md font-medium">
              {dataObjectFull?.pos?.store?.name}
            </div>
            <div className="text-gray-90 text-center text-md mb-4">
              {dataObjectFull?.pos?.store?.address
                ? dataObjectFull?.pos?.store.address
                : "-"}
            </div>
            {process.env.REACT_APP_CUSTOMER !== "SKAPPSK" && (
              <div className="flex flex-wrap gap-x-4 justify-between ">
                <div className="text-gray-500  text-left">
                  <Trans>Fiscal card ID:</Trans>
                </div>
                <div className="text-gray-90 text-left">
                  {dataObjectFull?.pos?.fiscalCardId}
                </div>
              </div>
            )}
            <div className=" flex flex-wrap gap-x-4 justify-between">
              <div className="text-gray-500 text-left">
                <Trans>Date:</Trans>
              </div>
              <div className="text-gray-90 text-left">
                {dataObject?.date ? dataObject?.date.substring(0, 10) : "-"}
              </div>
            </div>
            <div className=" flex flex-wrap gap-x-4 justify-between">
              <div className="text-gray-500 text-left">
                <Trans>Time:</Trans>
              </div>
              <div className="text-gray-90 text-left">
                {dataObject?.date ? dataObject?.date.substring(11, 19) : "-"}
              </div>
            </div>
            <div className="flex flex-wrap gap-x-4 justify-between ">
              <div className="text-gray-500 text-left">
                <Trans>Receipt number:</Trans>
              </div>
              <div className="text-gray-90 text-left">
                {dataObject?.receiptNumber ? dataObject.receiptNumber : "-"}
              </div>
            </div>
            <div className="flex flex-wrap gap-x-4 justify-between ">
              <div className="text-gray-500 text-left">
                <Trans>Sale ID:</Trans>
              </div>
              <div className="text-gray-90 text-left">
                {dataObject?.salePublicId ? dataObject.salePublicId : "-"}
              </div>
            </div>
            <div className="flex flex-wrap gap-x-4 justify-between ">
              <div className="flex gap-x-2 items-center text-gray-500 text-left">
                <Trans>User name: </Trans>
              </div>
              <div className="text-gray-90 text-left uppercase">
                {dataObject?.userName}
              </div>
            </div>
          </div>

          <h2 className="text-gray-90 text-center font-medium text-md mt-2 mb-2">
            <Trans>Sale</Trans>
          </h2>

          <div className="mt-1 text-sm">
            {loader && <Loader />}
            {dataObjectFull?.productList.length ? (
              dataObjectFull.productList.map((item, index) => {
                if (item.markedLabels && item.markedLabels.length) {
                  return item.markedLabels.map(
                    (mark: string, subIndex: number) => {
                      const vatForItem =
                        (((item.customTotal
                          ? item.customTotal / item.amount
                          : item.price) -
                          (item.discountAmount || 0)) *
                          (item.tax ? item.tax : item.product.vat)) /
                        (1 + (item.tax ? item.tax : item.product.vat));

                      return (
                        <CheckProductItem
                          key={subIndex + index}
                          name={item.productName || item.product.name}
                          amount={1}
                          price={item.price}
                          customTotal={
                            item.customTotal
                              ? item.customTotal / item.amount
                              : item.price
                          }
                          units={item.product.units}
                          vat={item.tax ? item.tax : item.product.vat}
                          vatForItem={vatForItem}
                          psid={item.psid ? item.psid : item.product.psid}
                          marked={mark}
                          barcodes={item.barcodes}
                          commissionTIN={item.commissionTIN || undefined}
                          commissionPINFL={item.commissionPINFL || undefined}
                        />
                      );
                    },
                  );
                } else {
                  const vatForItem =
                    (((item.customTotal
                      ? item.customTotal / item.amount
                      : item.price) -
                      (item.discountAmount || 0)) *
                      (item.tax ? item.tax : item.product.vat)) /
                    (1 + (item.tax ? item.tax : item.product.vat));

                  return (
                    <CheckProductItem
                      key={index}
                      name={item.productName || item.product.name}
                      amount={item.amount}
                      price={item.price}
                      customTotal={item.customTotal}
                      units={item.product.units}
                      vat={item.tax ? item.tax : item.product.vat}
                      vatForItem={vatForItem * item.amount}
                      psid={item.psid ? item.psid : item.product.psid}
                      barcodes={item.barcodes}
                      commissionTIN={item.commissionTIN || undefined}
                      commissionPINFL={item.commissionPINFL || undefined}
                    />
                  );
                }
              })
            ) : (
              <div>
                <Trans>The product list is empty</Trans>
              </div>
            )}
          </div>

          <div className="border-b pb-1 text-sm">
            <div className="flex flex-wrap gap-x-4 justify-between">
              <div className="text-gray-500 text-left">
                <Trans>Discount amount:</Trans>
              </div>
              <div className="text-gray-900 text-left">
                {numberCurrencyFormat(+dataObject.discountAmount.toFixed(2))}
              </div>
            </div>
            <div className="flex flex-wrap gap-x-4 justify-between">
              <div className="text-left text-gray-500">
                <Trans>Full cost</Trans>
              </div>
              <div className="text-gray-900 text-left">
                {numberCurrencyFormat(dataObject.amount)}
              </div>
            </div>
            {process.env.REACT_APP_CUSTOMER !== "SKAPPSK" && (
              <div className="flex flex-wrap gap-x-4 justify-between">
                <div className="text-gray-500 text-left">
                  <Trans>VAT including</Trans>:
                </div>
                <div className="text-gray-900 text-left">
                  {numberCurrencyFormat(+vat.toFixed(2))}
                </div>
              </div>
            )}
            <div className="flex flex-wrap gap-x-4 justify-between ">
              <div className="text-left font-medium text-lg text-gray-900 ">
                <Trans>Total</Trans>:
              </div>
              <div className="text-gray-900 text-left font-medium text-lg">
                {numberCurrencyFormat(
                  +(dataObject.amount - dataObject.discountAmount).toFixed(2),
                )}
              </div>
            </div>
            <div className="flex flex-wrap gap-x-4 justify-between">
              <div className="text-gray-500 text-left">
                <Trans>Payment method:</Trans>
              </div>
              <div className="text-gray-900 text-right">
                <div className="flex flex-col justify-center divide-y">
                  {dataObject.payments.map((item, index) => (
                    <div key={index}>
                      <p>
                        <span className="text-gray-900">
                          {item.paymentType.toLowerCase() === "cash" ? (
                            <Trans>by cash</Trans>
                          ) : (
                            <Trans>by cashless</Trans>
                          )}
                          :
                        </span>
                        <span className="text-gray-900 ">
                          {" "}
                          {numberCurrencyFormat(item.paymentAmount)}
                        </span>
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="border-b pb-1 text-xs">
            <div className="flex flex-wrap gap-x-4 justify-between">
              <div className="flex gap-x-2 items-center text-gray-500 text-left">
                <Trans>Cash desk number:</Trans>
              </div>
              <div className="text-gray-90 text-left">
                {dataObjectFull?.pos?.posNumber}
              </div>
            </div>
            <div className="flex flex-wrap gap-x-4 justify-between">
              <div className="flex gap-x-2 items-center text-gray-500 text-left">
                <Trans>Cash desk serial number:</Trans>
              </div>
              <div className="text-gray-90 text-left">
                {dataObjectFull?.pos?.posHardwareSerialNumber}
              </div>
            </div>
            <div className="flex flex-wrap gap-x-4 justify-between">
              <div className="flex gap-x-2 items-center text-gray-500 text-left">
                <Trans>Cash desk model:</Trans>
              </div>
              <div className="text-gray-90 text-left">
                {dataObjectFull?.pos?.posHardwareModel}
              </div>
            </div>
          </div>

          <div className="text-xs">
            <div className="flex flex-wrap gap-x-4 justify-between">
              <div className="flex gap-x-2 items-center text-gray-500 text-left">
                <Trans>Applet version:</Trans>
              </div>
              <div className="text-gray-90 text-left">
                {dataObject.appletVersion ? dataObject.appletVersion : "-"}
              </div>
            </div>
            {process.env.REACT_APP_CUSTOMER !== "SKAPPSK" && (
              <div className="flex flex-wrap gap-x-4 justify-between">
                <div className="flex gap-x-2 items-center text-gray-500 text-left">
                  <Trans>Fiscal module ID:</Trans>
                </div>
                <div className="text-gray-90 text-left">
                  {dataObjectFull?.pos.fiscalCardId
                    ? dataObjectFull.pos.fiscalCardId
                    : "-"}
                </div>
              </div>
            )}

            {process.env.REACT_APP_CUSTOMER !== "SKAPPSK" && isSuperAdmin && (
              <div className="flex flex-wrap gap-x-4 justify-between">
                <div className="flex gap-x-2 items-center text-gray-500 text-left">
                  <Trans>Fiscal sign:</Trans>
                </div>
                <div className="text-gray-90 text-left">
                  {dataObject.fiscalSign ? dataObject.fiscalSign : "-"}
                </div>
              </div>
            )}
          </div>

          {isSuperAdmin && (
            <div className="flex justify-center mt-5">
              <a href={qrLink} rel="noreferrer" target="_blank">
                <QRCode value={qrLink} />
              </a>
            </div>
          )}

          <div className="flex justify-end print:hidden">
            <div className="flex gap-4 mt-5 mr-5 sm:mt-4 sm:flex sm:flex-row-reverse">
              <Button
                type="button"
                secondary={true}
                label={<Trans>Print</Trans>}
                onClick={() => {
                  // console.log('+++', userInfo?.authorizedUser?.roles)
                  // console.log('---', isSuperAdmin)
                  window.print();
                  setOpenPopup(false);
                }}
              />
            </div>

            <div className="flex gap-4 mt-5 sm:mt-4 sm:flex sm:flex-row-reverse ">
              <Button
                type="button"
                secondary={true}
                label={<Trans>Cancel</Trans>}
                onClick={() => setOpenPopup(false)}
              />
            </div>
          </div>
        </div>
      ) : (
        <Alert type="error" title={<Trans>Have no data.</Trans>} />
      )}
    </>
  );
};
